import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Logout,
  Menu as MenuIcon,
  Refresh,
} from '@mui/icons-material';
import { useAuth } from '../../utils/AuthContext';
import NotificationBar from '../NotificationBar';
import { tokens, roleAccent, roleLabel } from '../../theme';

const { topBarHeight, sidebarWidth, sidebarCollapsedWidth, contentGutter, contentMaxWidth } =
  tokens.layout;

/**
 * The single application shell for every role.
 *
 * Previously each dashboard built its own header/sidebar and guessed the
 * header height with a different magic number (48 / 96 / 120 / 140px), so
 * admin, vendor and panelist looked like three unrelated products and the
 * admin sidebar overlapped its own content. All shell geometry now derives
 * from `tokens.layout`, so the sidebar, top bar and content can't drift.
 *
 * Props
 *  role            'admin' | 'vendor' | 'panelist'
 *  sections        [{ label, icon, badge? }] — the one source of truth for
 *                  navigation. Desktop rail and mobile drawer both read it,
 *                  so their indices can never disagree.
 *  activeIndex     currently selected section
 *  onSectionChange (index) => void
 *  actions         [{ label, icon, onClick, ... }] rendered in the top bar
 *  onRefresh       optional; shows a refresh control
 *  loading         shows the top-bar progress line
 *  error           optional error message shown above the content
 */
const AppShell = ({
  role = 'admin',
  sections = [],
  activeIndex = 0,
  onSectionChange = () => {},
  actions = [],
  onRefresh,
  loading = false,
  error = null,
  children,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);

  const accent = roleAccent[role] || tokens.color.accent;
  const railWidth = collapsed ? sidebarCollapsedWidth : sidebarWidth;
  const activeSection = sections[activeIndex];

  const handleLogout = async () => {
    setUserMenuAnchor(null);
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const handleNavigate = (index) => {
    onSectionChange(index);
    setMobileOpen(false);
  };

  const initials = (user?.username || '?').trim().charAt(0).toUpperCase();

  // -- Sidebar -------------------------------------------------------------
  const sidebarContent = (showLabels) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: tokens.color.surface }}>
      {/* Product identity. Role is a quiet badge here rather than a
          page-wide colour scheme, so all three roles read as one product. */}
      <Box
        sx={{
          height: topBarHeight,
          px: showLabels ? 2 : 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          borderBottom: `1px solid ${tokens.color.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: `${tokens.radius.sm}px`,
            bgcolor: accent,
            color: '#fff',
            display: 'grid',
            placeItems: 'center',
            fontSize: 13,
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          T
        </Box>
        {showLabels && (
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2 }} noWrap>
              TalentMesh
            </Typography>
            <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }} noWrap>
              {roleLabel[role] || role}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Navigation */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5 }}>
        {showLabels && (
          <Typography
            variant="overline"
            sx={{ display: 'block', px: 2, pb: 0.75, color: tokens.color.inkTertiary }}
          >
            Workspace
          </Typography>
        )}
        <List disablePadding sx={{ px: showLabels ? 1 : 0.75 }}>
          {sections.map((section, index) => {
            const selected = index === activeIndex;
            const button = (
              <ListItemButton
                key={section.label}
                selected={selected}
                onClick={() => handleNavigate(index)}
                sx={{
                  mb: 0.25,
                  px: showLabels ? 1.25 : 0,
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  // A left indicator marks selection without shifting the row,
                  // avoiding the translateX jump the admin rail used to do.
                  '&::before': selected
                    ? {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 6,
                        bottom: 6,
                        width: 2,
                        borderRadius: 2,
                        bgcolor: accent,
                      }
                    : undefined,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: showLabels ? 30 : 'auto',
                    '& .MuiSvgIcon-root': { fontSize: 19 },
                  }}
                >
                  {section.badge ? (
                    <Badge badgeContent={section.badge} color="error" max={99}>
                      {section.icon}
                    </Badge>
                  ) : (
                    section.icon
                  )}
                </ListItemIcon>
                {showLabels && <ListItemText primary={section.label} slotProps={{ primary: { noWrap: true } }} />}
              </ListItemButton>
            );

            return showLabels ? (
              button
            ) : (
              <Tooltip key={section.label} title={section.label} placement="right">
                {button}
              </Tooltip>
            );
          })}
        </List>
      </Box>

      {/* Collapse control — an explicit toggle rather than hover-expand,
          which used to shift the whole content area on mouse-over. */}
      {isDesktop && (
        <>
          <Divider />
          <Box sx={{ p: 0.75, display: 'flex', justifyContent: showLabels ? 'flex-end' : 'center', flexShrink: 0 }}>
            <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
              <IconButton size="small" onClick={() => setCollapsed((v) => !v)}>
                {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.color.canvas }}>
      {/* -- Top bar ------------------------------------------------------ */}
      <Box
        component="header"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          left: { xs: 0, md: `${railWidth}px` },
          height: topBarHeight,
          bgcolor: tokens.color.surface,
          borderBottom: `1px solid ${tokens.color.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: { xs: 2, md: `${contentGutter}px` },
          zIndex: theme.zIndex.appBar,
          transition: theme.transitions.create('left', { duration: 200 }),
        }}
      >
        {!isDesktop && (
          <IconButton edge="start" onClick={() => setMobileOpen(true)} sx={{ ml: -0.5 }}>
            <MenuIcon />
          </IconButton>
        )}

        {/* Current section is the page title. One h-level, one colour. */}
        <Typography variant="h4" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {activeSection?.label || ''}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {actions.map((action, i) => (
            <Tooltip key={i} title={action.tooltip || ''}>
              <span>
                <Button
                  size="small"
                  variant={action.variant || 'outlined'}
                  startIcon={action.icon}
                  onClick={action.onClick}
                  disabled={action.disabled}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  {action.label}
                </Button>
              </span>
            </Tooltip>
          ))}

          {onRefresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={onRefresh} disabled={loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          <NotificationBar />

          <Tooltip title={user?.username || 'Account'}>
            <IconButton size="small" onClick={(e) => setUserMenuAnchor(e.currentTarget)} sx={{ ml: 0.25 }}>
              <Avatar sx={{ width: 26, height: 26, bgcolor: accent, fontSize: 12 }}>{initials}</Avatar>
            </IconButton>
          </Tooltip>
        </Box>

        {/* Progress sits on the bar's bottom edge so it never reflows content. */}
        {loading && (
          <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, bottom: -1 }} />
        )}
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <Box sx={{ px: 1.75, py: 1.25 }}>
          <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600 }} noWrap>
            {user?.username || '—'}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }} noWrap>
            {user?.email || ''}
          </Typography>
          <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
            {roleLabel[role] || role}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ mt: 0.5, color: tokens.color.danger }}>
          <ListItemIcon sx={{ color: 'inherit' }}>
            <Logout fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>

      {/* -- Sidebar ------------------------------------------------------ */}
      {isDesktop ? (
        <Box
          component="nav"
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            width: railWidth,
            borderRight: `1px solid ${tokens.color.border}`,
            zIndex: theme.zIndex.appBar + 1,
            transition: theme.transitions.create('width', { duration: 200 }),
            overflow: 'hidden',
          }}
        >
          {sidebarContent(!collapsed)}
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          slotProps={{ paper: { sx: { width: sidebarWidth, borderRight: `1px solid ${tokens.color.border}` } } }}
        >
          {sidebarContent(true)}
        </Drawer>
      )}

      {/* -- Content ------------------------------------------------------ */}
      <Box
        component="main"
        sx={{
          ml: { xs: 0, md: `${railWidth}px` },
          pt: `${topBarHeight}px`,
          transition: theme.transitions.create('margin-left', { duration: 200 }),
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            maxWidth: contentMaxWidth,
            px: { xs: 2, md: `${contentGutter}px` },
            py: { xs: 2, md: 3 },
            // Vertical rhythm for whatever each dashboard renders, so sections
            // are evenly spaced without every page inventing its own margins.
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          {error && <Alert severity="error">{error}</Alert>}
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default AppShell;
